import React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { useReactToPrint } from 'react-to-print';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components/macro';
import {
  MdEdit,
  MdEmail,
  MdFileDownload,
  MdKeyboardArrowLeft,
} from 'react-icons/md';
import {
  useCustomReportsContext,
  loadCustomReport,
  useAuth,
} from 'lib/context';
import { LoadingIndicator, Tabs } from 'lib/components';
import { Tab } from 'lib/components/tabs';
import { downloadCustomReportData } from 'lib/api';
import { MenuDropdown } from 'lib/components/page/MenuDropdown';
import { Field, ModalCustomTableFields } from 'lib/components/modal';
import {
  CustomReportModal,
  SendReportModal,
  timeRangeConstants,
  UsersSelectorModal,
} from '../../components';
import { ReportTab } from './ReportTab';
import { RecurringTab } from './RecurringTab';
import { HistoryTab } from './HistoryTab';
import { reportData } from 'lib/const';
import { CustomersSelectorModal } from '../../components/CustomersSelectorModal';
import { NotFound } from 'app/pages/notFound/NotFound';
import { MainContainer } from '../index.styled';
import { Heading } from 'lib/components/styles/typography';
import { Gap, HeaderWrapper } from 'lib/components/styles/layout';
import { Container, MainWrapper } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';

const ArrowWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  & .tab-container {
    max-width: 100%;
  }
  & .tab-navigation {
    margin-top: 32px;
    padding: 0;
    justify-content: flex-start;
    max-width: 100%;
  }
  & .tab {
    margin-right: 32px;

    font-size: 18px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
  }
  & .tab-content {
    padding: 0;
    width: 100%;
  }
`;

const allReports: Field[] = Object.keys(reportData).map(reportValue => ({
  value: reportValue,
  // @ts-ignore
  label: reportData[reportValue].label,
}));

interface MatchParams {
  reportId: string;
}

export const Details = withRouter((props: RouteComponentProps<MatchParams>) => {
  const [showCustomizeReportModal, setShowCustomizeReportModal] =
    React.useState(false);
  const [showUserSelectorModal, setShowUserSelectorModal] =
    React.useState(false);
  const [selectedUsers, setSelectedUsers] = React.useState([] as string[]);
  const [showEditReportModal, setShowEditReportModal] = React.useState(false);
  const [initialReports, setInitialReports] = React.useState<Field[]>([]);
  const [showSendReportModal, setShowSendReportModal] = React.useState(false);
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [isCustomRange, setIsCustomRange] = React.useState<boolean>(false);
  const [dateRange, setDateRange] = React.useState('');
  const [downloadDropdownOpen, setDownloadDropdownOpen] = React.useState(false);
  const [showCustomerSelectorModal, setShowCustomerSelectorModal] =
    React.useState(false);

  const {
    userData: { access, isResellerAdmin },
  } = useAuth();
  const isCompanyAdmin = access === '3' || access === '4';

  const history = useHistory();

  const { reportId } = props.match.params;

  const {
    state: { customReports, loading },
    dispatch,
  } = useCustomReportsContext();

  const customReport = customReports[reportId];
  const sectionName = `Custom Report Details ${
    (customReport && customReport.reportId) || ''
  }`;

  React.useEffect(() => {
    loadCustomReport({ reportId, dispatch });
  }, [reportId]);

  React.useEffect(() => {
    if (customReport) {
      if (customReport.reports && customReport.reports.length) {
        const reports: Field[] = [];

        customReport.reports.forEach((report: string) => {
          report = report.includes('video-attributes')
            ? report.split('~')[0]
            : report;
          // @ts-ignore
          const reportDetails = reportData[report];
          if (reportDetails) {
            reports.push({
              value: report,
              label: reportDetails.label,
            });
          }
        });

        setInitialReports(reports);
      }

      if (customReport.range) {
        setDateRange(customReport.range);
        setIsCustomRange(false);
        if (customReport.range === timeRangeConstants.CUSTOM) {
          setIsCustomRange(true);
          setStartDate(
            !customReport.startDate
              ? new Date()
              : new Date(customReport.startDate)
          );
          setEndDate(
            !customReport.endDate ? new Date() : new Date(customReport.endDate)
          );
        }
      }

      if (customReport.users && customReport.users.length) {
        setSelectedUsers(customReport.users);
      }
    }
  }, [customReport]);

  const onDateRangeChange = (start: Date, end: Date, range: string) => {
    if (range !== dateRange) {
      setStartDate(start);
      setEndDate(end);
      setDateRange(range);
    }
  };

  const handleSendReportModalClose = async () => {
    loadCustomReport({ reportId, dispatch });
    setShowSendReportModal(false);
  };

  const reportTabRef = React.useRef(null);
  const downloadReportScreenshot = useReactToPrint({
    content: () => reportTabRef.current,
  });

  const [activeTab, setActiveTab] = React.useState(0);

  const downloadReportCsv = () => {
    if (customReport && startDate && endDate) {
      return downloadCustomReportData(
        customReport.reportId || '',
        `${customReport.title}.csv`,
        startDate,
        endDate
      );
    }
  };

  const modalCustomTableProps = {};
  if (isResellerAdmin) {
    // @ts-ignore
    modalCustomTableProps.onBackClick = () => {
      setShowCustomizeReportModal(false);
      setShowCustomerSelectorModal(true);
    };
  } else if (isCompanyAdmin) {
    // @ts-ignore
    modalCustomTableProps.onBackClick = () => {
      setShowCustomizeReportModal(false);
      setShowUserSelectorModal(true);
    };
  }

  let tabs: Tab[] = [
    {
      title: 'Report',
      component: (
        <div ref={reportTabRef}>
          <ReportTab
            customReport={customReport}
            from={startDate}
            to={endDate}
            dateRange={dateRange}
            onDateRangeChange={onDateRangeChange}
          />
        </div>
      ),
    },
    ...(!isCustomRange
      ? [
          {
            title: 'Recurring',
            component: <RecurringTab customReport={customReport} />,
          },
        ]
      : []),
    {
      title: 'History',
      component: <HistoryTab customReport={customReport} />,
    },
  ];

  if (loading) {
    return (
      <MainContainer>
        <LoadingIndicator isLoading={loading} height='300px' />
      </MainContainer>
    );
  }

  if (!customReport) {
    return <NotFound style={{ marginTop: '-80px' }} />;
  }

  return (
    <>
      <Container>
        <MainWrapper resetPadding={false}>
          <HeaderWrapper>
            <Gap gap='2px'>
              <ArrowWrapper onClick={() => history.goBack()}>
                <MdKeyboardArrowLeft size={24} />
              </ArrowWrapper>
              <Heading>{customReport.title}</Heading>
            </Gap>

            <Gap>
              <Button
                text='Send'
                icon={<MdEmail size={20} />}
                onClick={() => setShowSendReportModal(true)}
              />
              <Button
                text='Download'
                icon={<MdFileDownload size={20} />}
                onClick={() => {
                  // show download menu for admins
                  if (isCompanyAdmin) {
                    setDownloadDropdownOpen(true);
                  } else {
                    if (downloadReportScreenshot) {
                      downloadReportScreenshot();
                    }
                  }
                }}
              />
              <Button
                text='Edit'
                icon={<MdEdit width={20} />}
                onClick={() => {
                  if (isResellerAdmin) {
                    setShowCustomerSelectorModal(true);
                  } else if (customReport.users && customReport.users.length) {
                    setShowUserSelectorModal(true);
                  } else {
                    setShowCustomizeReportModal(true);
                  }
                }}
              />

              <MenuDropdown
                aligment={true}
                items={[
                  {
                    title: 'PDF',
                    onItemClick: () => {
                      setDownloadDropdownOpen(false);
                      if (downloadReportScreenshot) {
                        downloadReportScreenshot();
                      }
                    },
                    path: '#',
                  },
                  {
                    title: 'CSV',
                    onItemClick: () => {
                      setDownloadDropdownOpen(false);
                      downloadReportCsv();
                    },
                    path: '#',
                  },
                ]}
                isDropdownOpen={downloadDropdownOpen}
                OnClickOutside={() => setDownloadDropdownOpen(false)}
              />
            </Gap>
          </HeaderWrapper>
          <Content>
            <Tabs
              tabs={tabs}
              defaultActiveTab={activeTab}
              onChange={setActiveTab}
            />
          </Content>
          {showCustomizeReportModal && (
            <ModalCustomTableFields
              title='New Custom Report'
              onClose={() => setShowCustomizeReportModal(false)}
              tableName={sectionName}
              onSave={fields => {
                setInitialReports(fields);
                setShowEditReportModal(true);
              }}
              allFields={allReports}
              initialFields={initialReports}
              btnText='Continue'
              saveToLocalStorage={false}
              {...modalCustomTableProps}
            />
          )}

          {showEditReportModal && (
            <CustomReportModal
              initReport={customReport}
              reports={initialReports}
              handleModalClose={() => setShowEditReportModal(false)}
              goToReportDetails={false}
              users={selectedUsers}
              onBackClick={() => {
                setShowEditReportModal(false);
                setShowCustomizeReportModal(true);
              }}
            />
          )}

          {showSendReportModal && startDate && endDate && (
            <SendReportModal
              handleModalClose={() => handleSendReportModalClose()}
              startDate={startDate}
              endDate={endDate}
              range={dateRange}
              reportId={customReport.reportId}
            />
          )}

          {showUserSelectorModal && (
            <UsersSelectorModal
              handleModalClose={() => setShowUserSelectorModal(false)}
              initUsers={customReport.users}
              onSelect={userIds => {
                setShowUserSelectorModal(false);
                setSelectedUsers(userIds);
                setShowCustomizeReportModal(true);
              }}
            />
          )}

          {showCustomerSelectorModal && (
            <CustomersSelectorModal
              handleModalClose={() => setShowCustomerSelectorModal(false)}
              onSelect={userIds => {
                setShowCustomerSelectorModal(false);
                setSelectedUsers(userIds);
                setShowCustomizeReportModal(true);
              }}
              initUsers={selectedUsers}
            />
          )}
        </MainWrapper>
      </Container>
    </>
  );
});
